import type { AgentMessage } from '@/hooks/use-agent-chat.hook';

/**
 * How a turn that paused at a limit of its own reads in the thread (the chat
 * continues such a turn by itself, up to three times: use-agent-chat.hook).
 */

/** The id of the Continue reply the server ends a paused turn with. */
export const CONTINUE_CALL_ID = 'turn-limit-continue';

/** What shows for a Continue the chat sent itself. */
export const KEPT_GOING = 'Clu kept going';

/** The line before the Continue button a paused turn is left with. */
export const PAUSED_HERE = 'I paused here. Continue?';

/** The recap list a paused turn ends its text with. */
const RECAP = /(?:^|\n+)Done so far:\n(?:- [^\n]*(?:\n|$))+$/;

/** The text without the recap list a paused turn ends with. */
export function withoutRecap(text: string): string {
  return text.trimEnd().replace(RECAP, '').trimEnd();
}

/**
 * The messages with each reply that the chat went on from by itself marked
 * (`continuedOn`): an assistant message directly followed by a Continue the
 * chat sent. Messages that need no mark come back as they are.
 */
export function markContinuedOn(messages: AgentMessage[]): AgentMessage[] {
  let marked: AgentMessage[] | null = null;
  messages.forEach((message, index) => {
    if (
      message.role !== 'assistant' ||
      message.continuedOn ||
      !messages[index + 1]?.autoContinue
    )
      return;
    marked ??= [...messages];
    marked[index] = { ...message, continuedOn: true };
  });
  return marked ?? messages;
}

/** The server's own words for when the workspace's day ends ("midnight UTC",
 *  or its clock time in UTC). */
const SERVER_RESET = /\bresets at (?:midnight|\d{1,2}:\d{2}) UTC\b/i;

/** A time of day in the viewer's own zone, "5:00 PM"; null for a bad date. */
export function localTimeOf(iso: string): string | null {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  return at.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

/**
 * The reply's text as the thread shows it: without the recap list when the
 * chat went on from this reply by itself (the next turn's steps carry on from
 * it), and with the daily allowance's reset in the viewer's own time ("resets
 * at 5:00 PM") in place of the server's "midnight UTC", which stays when
 * there is no time to show.
 */
export function shownText(
  message: Pick<AgentMessage, 'content' | 'continuedOn' | 'resetsAt'>
): string {
  let text = message.content ?? '';
  if (message.continuedOn) text = withoutRecap(text);
  const time = message.resetsAt ? localTimeOf(message.resetsAt) : null;
  return time ? text.replace(SERVER_RESET, `resets at ${time}`) : text;
}
